import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from '../hooks/useMediaQuery';

/**
 * The top bar's page actions (docs/ux/desktop-layout-rules.md §1, §9.9; the Impeccable critique §4 item 1): a
 * room's primary action and its freshness line sit beside the breadcrumb at ≥1024 and in the body, under the
 * title, below. One DOM node at a time — the page renders `<PageActions>` where the phone wants the control and
 * the component portals it into the bar's slot on a desktop (`matchMedia`, the same 1024 the CSS uses), so a
 * spec finds the control by its test id, role and name at every width and nothing is duplicated.
 */
const DESKTOP = '(min-width: 1024px)';

interface ActionsState {
  slot: HTMLElement | null;
  setSlot: (el: HTMLElement | null) => void;
}

const ActionsContext = createContext<ActionsState>({ slot: null, setSlot: () => undefined });

export function ActionsProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ slot, setSlot }), [slot]);
  return <ActionsContext.Provider value={value}>{children}</ActionsContext.Provider>;
}

/** The slot in the top bar: the shell renders it once, between the breadcrumb and the bar's own buttons. */
export function TopbarActionsSlot({ label }: { label: string }) {
  const { setSlot } = useContext(ActionsContext);
  return <div className="tb-actions" role="group" aria-label={label} ref={setSlot} data-testid="page-actions" />;
}

/**
 * A page's actions, rendered where the phone wants them. At ≥1024 the same children move into the top bar; the
 * wrapper keeps its `data-testid` at both widths. `desktopOnly` is for a room whose body already *is* the action
 * (the Workspace composer): the bar gets a button, the phone nothing extra.
 */
export function PageActions({ children, className, testId, desktopOnly = false }: { children: ReactNode; className?: string; testId?: string; desktopOnly?: boolean }) {
  const { slot } = useContext(ActionsContext);
  const desktop = useMediaQuery(DESKTOP);
  if (desktop) {
    // The slot's ref lands in the same commit as the page's first render; the second render — before paint — portals.
    if (!slot) return null;
    return createPortal(
      <div className="tb-group" data-testid={testId}>
        {children}
      </div>,
      slot,
    );
  }
  if (desktopOnly) return null;
  return (
    <div className={className ? `page-actions ${className}` : 'page-actions'} data-testid={testId}>
      {children}
    </div>
  );
}
